import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SectionItem {
  id?: number;
  item_key: string;
  display_order: number;
  is_active: boolean;
  content: {
    en: {
      title: string;
      description: string;
      metadata?: any;
    };
    ar: {
      title: string;
      description: string;
      metadata?: any;
    };
  };
  icon?: string;
  config?: any;
}

interface Section {
  id?: number;
  section_type: string;
  section_key: string;
  display_order: number;
  is_active: boolean;
  content: {
    en: {
      title: string;
      subtitle: string;
      content: string;
    };
    ar: {
      title: string;
      subtitle: string;
      content: string;
    };
  };
  items: SectionItem[];
  config?: any;
}

interface SectionManagerProps {
  sections: Section[];
  onSectionsChange: (sections: Section[]) => void;
  language: 'en' | 'ar';
  onLanguageChange: (lang: 'en' | 'ar') => void;
}

const SectionManager: React.FC<SectionManagerProps> = ({
  sections,
  onSectionsChange,
  language,
  onLanguageChange
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Helper function to parse metadata (handles both string and object formats)
  const parseMetadata = (metadata: any): any => {
    if (!metadata) return {};
    if (typeof metadata === 'object') return metadata;
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch (e) {        return {};
      }
    }
    return {};
  };

  // Helper function to ensure content structure is properly initialized
  const ensureContentStructure = (section: Section): Section => {
    return {
      ...section,
      content: {
        en: {
          title: section.content?.en?.title || '',
          subtitle: section.content?.en?.subtitle || '',
          content: section.content?.en?.content || ''
        },
        ar: {
          title: section.content?.ar?.title || '',
          subtitle: section.content?.ar?.subtitle || '',
          content: section.content?.ar?.content || ''
        }
      },
      items: section.items?.map(item => ({
        ...item,
        content: {
          en: {
            title: item.content?.en?.title || '',
            description: item.content?.en?.description || '',
            metadata: parseMetadata(item.content?.en?.metadata)
          },
          ar: {
            title: item.content?.ar?.title || '',
            description: item.content?.ar?.description || '',
            metadata: parseMetadata(item.content?.ar?.metadata)
          }
        }
      })) || []
    };
  };

  // Section type definitions with their characteristics
  const sectionTypes = {
    hero: {
      name: 'Hero Section',
      nameAr: 'قسم البطل',
      icon: '🌟',
      hasItems: false,
      description: 'Main hero section with title, subtitle and CTAs'
    },
    benefits: {
      name: 'Benefits Section',
      nameAr: 'قسم المزايا',
      icon: '✨',
      hasItems: true,
      description: 'Why choose this country - benefit cards with icons'
    },
    sectors: {
      name: 'Growing Sectors',
      nameAr: 'القطاعات النامية',
      icon: '📈',
      hasItems: true,
      description: 'Fastest-growing business sectors with growth percentages'
    },
    business_types: {
      name: 'Business Structure',
      nameAr: 'أنواع الكيانات التجارية',
      icon: '🏢',
      hasItems: true,
      description: 'Different business entity types and requirements'
    },
    packages: {
      name: 'Packages Section',
      nameAr: 'قسم الباقات',
      icon: '📦',
      hasItems: false,
      description: 'Dynamic packages display (managed separately)'
    },
    requirements: {
      name: 'Requirements',
      nameAr: 'المتطلبات',
      icon: '📄',
      hasItems: true,
      description: 'Required documents and materials list'
    },
    process: {
      name: 'Process Steps',
      nameAr: 'خطوات العملية',
      icon: '⚡',
      hasItems: true,
      description: 'Step-by-step formation process'
    },
    faq: {
      name: 'FAQ Section',
      nameAr: 'الأسئلة الشائعة',
      icon: '❓',
      hasItems: true,
      description: 'Frequently asked questions'
    },
    states: {
      name: 'Popular States',
      nameAr: 'الولايات الشعبية',
      icon: '🏛️',
      hasItems: true,
      description: 'Popular states for incorporation with benefits'
    },
    cta: {
      name: 'Call to Action',
      nameAr: 'دعوة للعمل',
      icon: '🚀',
      hasItems: false,
      description: 'Final CTA section with contact info'
    }
  };

  const addSection = (sectionType: string) => {
    const typeInfo = sectionTypes[sectionType as keyof typeof sectionTypes];
    const newSection: Section = {
      section_type: sectionType,
      section_key: sectionType,
      display_order: sections.length + 1,
      is_active: true,
      content: {
        en: {
          title: typeInfo.name,
          subtitle: '',
          content: ''
        },
        ar: {
          title: typeInfo.nameAr,
          subtitle: '',
          content: ''
        }
      },
      items: [],
      config: { layout: 'default' }
    };

    onSectionsChange([...sections, newSection]);
  };

  const updateSection = (index: number, updatedSection: Section) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    onSectionsChange(newSections);
  };

  const deleteSection = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    onSectionsChange(newSections);
  };

  const addSectionItem = (sectionIndex: number) => {
    const section = sections[sectionIndex];
    const newItem: SectionItem = {
      item_key: `item_${section.items.length + 1}`,
      display_order: section.items.length + 1,
      is_active: true,
      content: {
        en: { title: '', description: '', metadata: {} },
        ar: { title: '', description: '', metadata: {} }
      },
      icon: ''
    };

    const updatedSection = {
      ...section,
      items: [...section.items, newItem]
    };

    updateSection(sectionIndex, updatedSection);
  };

  const updateSectionItem = (sectionIndex: number, itemIndex: number, updatedItem: SectionItem) => {
    const section = sections[sectionIndex];
    const updatedItems = [...section.items];
    updatedItems[itemIndex] = updatedItem;

    const updatedSection = {
      ...section,
      items: updatedItems
    };

    updateSection(sectionIndex, updatedSection);
  };

  const deleteSectionItem = (sectionIndex: number, itemIndex: number) => {
    const section = sections[sectionIndex];
    const updatedItems = section.items.filter((_, i) => i !== itemIndex);

    const updatedSection = {
      ...section,
      items: updatedItems
    };

    updateSection(sectionIndex, updatedSection);
  };

  // Helper function to update item metadata
  const updateItemMetadata = (sectionIndex: number, itemIndex: number, metadataKey: string, value: string | boolean) => {
    const section = sections[sectionIndex];
    const item = section.items[itemIndex];

    const updatedItem = {
      ...item,
      content: {
        ...item.content,
        [language]: {
          ...item.content?.[language],
          metadata: {
            ...item.content?.[language]?.metadata,
            [metadataKey]: value
          }
        }
      }
    };

    updateSectionItem(sectionIndex, itemIndex, updatedItem);
  };

  // Render section-specific metadata fields
  const renderMetadataFields = (section: Section, item: SectionItem, sectionIndex: number, itemIndex: number) => {
    const currentMetadata = item.content?.[language]?.metadata || {};

    if (section.section_type === 'sectors') {
      return (
        <div className="mt-3 p-3 bg-blue-50 rounded border">
          <h5 className="text-sm font-semibold text-blue-800 mb-2">
            {language === 'ar' ? 'بيانات النمو' : 'Growth Data'}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'نسبة النمو' : 'Growth Percentage'}
              </label>
              <input
                type="text"
                value={currentMetadata.growth || ''}
                onChange={(e) => {
                  let value = e.target.value;
                  // Auto-format percentage if % is missing
                  if (value && !value.includes('%') && /^[+-]?\d+$/.test(value)) {
                    value += '%';
                  }
                  updateItemMetadata(sectionIndex, itemIndex, 'growth', value);
                }}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={language === 'ar' ? '+25%' : '+25%'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'ar' ? 'مثال: +25% أو -5%' : 'Example: +25% or -5%'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (section.section_type === 'business_types') {
      return (
        <div className="mt-3 p-3 bg-green-50 rounded border">
          <h5 className="text-sm font-semibold text-green-800 mb-2">
            {language === 'ar' ? 'بيانات التكوين' : 'Formation Data'}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'رأس المال الأدنى' : 'Min. Capital'}
              </label>
              <input
                type="text"
                value={currentMetadata.minCapital || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'minCapital', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-green-500 focus:border-green-500"
                placeholder={language === 'ar' ? 'SAR 10,000' : 'SAR 10,000'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'الملكية الأجنبية' : 'Foreign Ownership'}
              </label>
              <input
                type="text"
                value={currentMetadata.foreignOwnership || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'foreignOwnership', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-green-500 focus:border-green-500"
                placeholder={language === 'ar' ? '100% مسموح' : '100% allowed'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'الضرائب' : 'Taxation'}
              </label>
              <input
                type="text"
                value={currentMetadata.taxation || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'taxation', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-green-500 focus:border-green-500"
                placeholder={language === 'ar' ? 'معلومات الضرائب' : 'Tax info'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'المسؤولية' : 'Liability'}
              </label>
              <input
                type="text"
                value={currentMetadata.liability || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'liability', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-green-500 focus:border-green-500"
                placeholder={language === 'ar' ? 'معلومات المسؤولية' : 'Liability info'}
              />
            </div>
          </div>
        </div>
      );
    }

    if (section.section_type === 'requirements') {
      return (
        <div className="mt-3 p-3 bg-yellow-50 rounded border">
          <h5 className="text-sm font-semibold text-yellow-800 mb-2">
            {language === 'ar' ? 'بيانات المتطلبات' : 'Requirement Data'}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'وقت المعالجة' : 'Processing Time'}
              </label>
              <input
                type="text"
                value={currentMetadata.processingTime || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'processingTime', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                placeholder={language === 'ar' ? '3-5 أيام' : '3-5 days'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'عدد الوثائق' : 'Document Count'}
              </label>
              <input
                type="number"
                value={currentMetadata.documentCount || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'documentCount', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="3"
                min="0"
              />
            </div>
          </div>
        </div>
      );
    }

    if (section.section_type === 'process') {
      return (
        <div className="mt-3 p-3 bg-purple-50 rounded border">
          <h5 className="text-sm font-semibold text-purple-800 mb-2">
            {language === 'ar' ? 'بيانات العملية' : 'Process Data'}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'المدة' : 'Duration'}
              </label>
              <input
                type="text"
                value={currentMetadata.duration || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'duration', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={language === 'ar' ? '1-2 يوم' : '1-2 days'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'التكلفة' : 'Cost'}
              </label>
              <input
                type="text"
                value={currentMetadata.cost || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'cost', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={language === 'ar' ? '$500' : '$500'}
              />
            </div>
          </div>
        </div>
      );
    }

    if (section.section_type === 'benefits') {
      return (
        <div className="mt-3 p-3 bg-indigo-50 rounded border">
          <h5 className="text-sm font-semibold text-indigo-800 mb-2">
            {language === 'ar' ? 'بيانات المزايا' : 'Benefit Data'}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'نسبة النجاح' : 'Success Rate'}
              </label>
              <input
                type="text"
                value={currentMetadata.successRate || ''}
                onChange={(e) => {
                  let value = e.target.value;
                  // Auto-format percentage if % is missing
                  if (value && !value.includes('%') && /^\d+$/.test(value)) {
                    value += '%';
                  }
                  updateItemMetadata(sectionIndex, itemIndex, 'successRate', value);
                }}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder={language === 'ar' ? '95%' : '95%'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {language === 'ar' ? 'الإحصائية' : 'Statistic'}
              </label>
              <input
                type="text"
                value={currentMetadata.statistic || ''}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'statistic', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder={language === 'ar' ? '1000+ عميل' : '1000+ clients'}
              />
            </div>
          </div>
        </div>
      );
    }

    if (section.section_type === 'states') {
      return (
        <div className="mt-3 p-3 bg-teal-50 rounded border">
          <h5 className="text-sm font-semibold text-teal-800 mb-2">
            {language === 'ar' ? 'بيانات الولاية' : 'State Data'}
          </h5>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`popular-${sectionIndex}-${itemIndex}`}
                checked={currentMetadata.popular === true}
                onChange={(e) => updateItemMetadata(sectionIndex, itemIndex, 'popular', e.target.checked)}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor={`popular-${sectionIndex}-${itemIndex}`} className="ml-2 text-xs font-medium text-gray-600">
                {language === 'ar' ? 'ولاية شعبية (عرض شارة)' : 'Popular State (Show Badge)'}
              </label>
            </div>
          </div>
        </div>
      );
    }

    if (section.section_type === 'faq') {
      return (
        <div className="mt-3 p-3 bg-pink-50 rounded border">
          <h5 className="text-sm font-semibold text-pink-800 mb-2">
            {language === 'ar' ? 'بيانات السؤال' : 'FAQ Data'}
          </h5>
          <p className="text-xs text-gray-500">
            {language === 'ar'
              ? 'العنوان = السؤال، الوصف = الإجابة'
              : 'Title = Question, Description = Answer'}
          </p>
        </div>
      );
    }

    return null;
  };

  const moveSection = (fromIndex: number, toIndex: number) => {
    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);

    // Update display_order
    newSections.forEach((section, index) => {
      section.display_order = index + 1;
    });

    onSectionsChange(newSections);
  };

  const isRTL = language === 'ar';

  return (
    <div className="space-y-6">
      {/* Language Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Page Sections {language === 'ar' ? '(عربي)' : '(English)'}
        </h3>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => onLanguageChange('en')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              language === 'en'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🇺🇸 English
          </button>
          <button
            type="button"
            onClick={() => onLanguageChange('ar')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              language === 'ar'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🇸🇦 العربية
          </button>
        </div>
      </div>

      {/* Sections List */}
      <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <AnimatePresence>
          {sections.map((section, sectionIndex) => {
            const safeSection = ensureContentStructure(section);
            const typeInfo = sectionTypes[safeSection.section_type as keyof typeof sectionTypes];
            const isExpanded = expandedSection === safeSection.section_key;

            return (
              <motion.div
                key={safeSection.section_key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="border border-gray-200 rounded-lg bg-white"
              >
                {/* Section Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <div className="flex items-center space-x-3" dir="ltr">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{typeInfo?.icon}</span>
                      <h4 className="font-medium text-gray-900">
                        {language === 'ar' ? typeInfo?.nameAr : typeInfo?.name}
                      </h4>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Order: {safeSection.display_order}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2" dir="ltr">
                    {/* Move Up/Down */}
                    <button
                      type="button"
                      onClick={() => sectionIndex > 0 && moveSection(sectionIndex, sectionIndex - 1)}
                      disabled={sectionIndex === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move Up"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => sectionIndex < sections.length - 1 && moveSection(sectionIndex, sectionIndex + 1)}
                      disabled={sectionIndex === sections.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move Down"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Toggle Active */}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedSection = { ...safeSection, is_active: !safeSection.is_active };
                        updateSection(sectionIndex, updatedSection);
                      }}
                      className={`p-1 rounded ${
                        safeSection.is_active ? 'text-green-600' : 'text-gray-400'
                      }`}
                      title={safeSection.is_active ? 'Hide Section' : 'Show Section'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d={safeSection.is_active ? "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" : "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"}
                        />
                      </svg>
                    </button>

                    {/* Expand/Collapse */}
                    <button
                      type="button"
                      onClick={() => setExpandedSection(isExpanded ? null : safeSection.section_key)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => deleteSection(sectionIndex)}
                      className="p-1 text-red-400 hover:text-red-600"
                      title="Delete Section"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Section Content */}
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-4 space-y-4"
                  >
                    {/* Section Content Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'ar' ? 'العنوان' : 'Title'}
                        </label>
                        <input
                          type="text"
                          value={safeSection.content?.[language]?.title || ''}
                          onChange={(e) => {
                            const updatedSection = {
                              ...safeSection,
                              content: {
                                ...safeSection.content,
                                [language]: {
                                  ...safeSection.content?.[language],
                                  title: e.target.value
                                }
                              }
                            };
                            updateSection(sectionIndex, updatedSection);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder={language === 'ar' ? 'أدخل العنوان...' : 'Enter title...'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'ar' ? 'العنوان الفرعي' : 'Subtitle'}
                        </label>
                        <input
                          type="text"
                          value={safeSection.content?.[language]?.subtitle || ''}
                          onChange={(e) => {
                            const updatedSection = {
                              ...safeSection,
                              content: {
                                ...safeSection.content,
                                [language]: {
                                  ...safeSection.content?.[language],
                                  subtitle: e.target.value
                                }
                              }
                            };
                            updateSection(sectionIndex, updatedSection);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder={language === 'ar' ? 'أدخل العنوان الفرعي...' : 'Enter subtitle...'}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'المحتوى' : 'Content'}
                      </label>
                      <textarea
                        value={safeSection.content?.[language]?.content || ''}
                        onChange={(e) => {
                          const updatedSection = {
                            ...safeSection,
                            content: {
                              ...safeSection.content,
                              [language]: {
                                ...safeSection.content?.[language],
                                content: e.target.value
                              }
                            }
                          };
                          updateSection(sectionIndex, updatedSection);
                        }}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder={language === 'ar' ? 'أدخل المحتوى...' : 'Enter content...'}
                      />
                    </div>

                    {/* Section Items */}
                    {typeInfo?.hasItems && (
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="font-medium text-gray-900">
                            {language === 'ar' ? 'عناصر القسم' : 'Section Items'}
                          </h5>
                          <button
                            type="button"
                            onClick={() => addSectionItem(sectionIndex)}
                            className="text-sm bg-primary-600 text-white px-3 py-1 rounded-md hover:bg-primary-700 transition-colors"
                          >
                            {language === 'ar' ? '+ إضافة عنصر' : '+ Add Item'}
                          </button>
                        </div>

                        <div className="space-y-3">
                          {safeSection.items.map((item, itemIndex) => (
                            <div key={item.item_key} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-700">
                                  {language === 'ar' ? 'عنصر' : 'Item'} {itemIndex + 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => deleteSectionItem(sectionIndex, itemIndex)}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {language === 'ar' ? 'الأيقونة' : 'Icon'}
                                  </label>
                                  <input
                                    type="text"
                                    value={item.icon || ''}
                                    onChange={(e) => {
                                      const updatedItem = { ...item, icon: e.target.value };
                                      updateSectionItem(sectionIndex, itemIndex, updatedItem);
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="🚀"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {language === 'ar' ? 'العنوان' : 'Title'}
                                  </label>
                                  <input
                                    type="text"
                                    value={item.content?.[language]?.title || ''}
                                    onChange={(e) => {
                                      const updatedItem = {
                                        ...item,
                                        content: {
                                          ...item.content,
                                          [language]: {
                                            ...item.content?.[language],
                                            title: e.target.value
                                          }
                                        }
                                      };
                                      updateSectionItem(sectionIndex, itemIndex, updatedItem);
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    placeholder={language === 'ar' ? 'عنوان العنصر' : 'Item title'}
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {language === 'ar' ? 'الوصف' : 'Description'}
                                  </label>
                                  <textarea
                                    value={item.content?.[language]?.description || ''}
                                    onChange={(e) => {
                                      const updatedItem = {
                                        ...item,
                                        content: {
                                          ...item.content,
                                          [language]: {
                                            ...item.content?.[language],
                                            description: e.target.value
                                          }
                                        }
                                      };
                                      updateSectionItem(sectionIndex, itemIndex, updatedItem);
                                    }}
                                    rows={2}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                    placeholder={language === 'ar' ? 'وصف العنصر' : 'Item description'}
                                  />
                                </div>
                              </div>

                              {/* Section-specific Metadata Fields */}
                              {renderMetadataFields(section, item, sectionIndex, itemIndex)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Add Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'ar' ? 'إضافة قسم جديد' : 'Add New Section'}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(sectionTypes).map(([key, typeInfo]) => (
            <button
              key={key}
              type="button"
              onClick={() => addSection(key)}
              className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors group"
            >
              <span className="text-2xl mb-2 group-hover:scale-110 transition-transform">
                {typeInfo.icon}
              </span>
              <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700">
                {language === 'ar' ? typeInfo.nameAr : typeInfo.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SectionManager;